import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskText, MaskTextLoad, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES, MANIFESTO, FOUNDER, MENTORSHIP } from "@/data/content";

const PageIntro = () => (
  <section className="relative pt-40 md:pt-52 pb-16 px-5 md:px-10 grain overflow-hidden" data-testid="movement-hero">
    <div className="absolute -top-24 -left-24 h-[24rem] w-[24rem] rounded-full brand-gradient blur-[130px] opacity-20 animate-floaty" />
    <div className="relative z-10 max-w-[1400px] mx-auto">
      <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">The Movement</p>
      <h1 className="font-serif font-light text-[13vw] md:text-[7.5vw] leading-[0.86] text-ink">
        <MaskTextLoad lines={["A movement born", "from one conviction."]} />
      </h1>
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-2xl font-sans text-lg md:text-xl leading-relaxed text-ink/65">
          Every young woman deserves the opportunity to discover who she is — before the world tells her who she should become.
        </p>
      </FadeUp>
    </div>
  </section>
);

const Chapter = ({ m, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const reverse = i % 2 === 1;

  return (
    <section ref={ref} className="relative py-16 md:py-24 px-5 md:px-10" data-testid={`chapter-${m.no}`}>
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        {/* Image */}
        <div className={`relative ${reverse ? "md:order-2" : ""}`}>
          <div className="relative overflow-hidden rounded-3xl aspect-[4/5] card-shadow">
            <motion.img style={{ y }} src={m.image} alt={m.kicker} className="h-[116%] w-full object-cover" />
          </div>
          <span className="absolute -top-10 -left-2 font-serif font-light text-[8rem] md:text-[11rem] leading-none stroke-text-strong pointer-events-none">
            {m.no}
          </span>
        </div>

        {/* Text */}
        <div>
          <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta mb-5">{m.kicker}</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] text-ink">
            <MaskText lines={[m.title]} />
          </h2>
          <div className="mt-8 space-y-5 max-w-xl">
            {m.body.map((p, j) => (
              <FadeUp key={j} delay={0.04 * j}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-ink/70">{p}</p>
              </FadeUp>
            ))}
          </div>

          {m.questions && (
            <Stagger className="mt-8 space-y-3 max-w-xl" stagger={0.07}>
              {m.questions.map((q) => (
                <StaggerItem key={q} className="border-l-2 border-coral pl-5">
                  <span className="font-serif italic text-xl md:text-2xl text-ink/90">{q}</span>
                </StaggerItem>
              ))}
            </Stagger>
          )}
          {m.tail && (
            <FadeUp delay={0.1}>
              <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-ink/70 max-w-xl">{m.tail}</p>
            </FadeUp>
          )}

          {m.list && (
            <div className="mt-8">
              <FadeUp>
                <p className="font-serif italic text-xl text-ink/80 mb-4">{m.listIntro}</p>
              </FadeUp>
              <Stagger className="flex flex-wrap gap-2.5" stagger={0.06}>
                {m.list.map((li) => (
                  <StaggerItem key={li}>
                    <span className="inline-block rounded-full bg-white border border-ink/10 card-shadow px-5 py-2.5 font-display text-xs md:text-sm uppercase tracking-[0.1em] text-ink/80">
                      {li}
                    </span>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          )}

          <FadeUp delay={0.12}>
            <p className="mt-9 border-l-2 border-magenta pl-6 font-serif italic text-2xl md:text-3xl leading-snug text-ink">
              {m.pull}
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Founder = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);

  return (
    <section ref={ref} className="relative bg-[#1B0812] text-cream py-24 md:py-36 px-5 md:px-10 grain" data-testid="founder-section">
      <div className="relative z-10 max-w-[1400px] mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-start">
        <div className="md:sticky md:top-28">
          <div className="relative overflow-hidden rounded-3xl aspect-[3/4]">
            <motion.img style={{ y }} src={IMAGES.founder} alt="The founder's reflection" className="h-[112%] w-full object-cover" />
          </div>
        </div>
        <div>
          <p className="font-display text-xs uppercase tracking-[0.3em] text-coral mb-6">{FOUNDER.kicker}</p>
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.05] text-cream">
            <MaskText lines={[FOUNDER.title]} />
          </h2>
          <div className="mt-10 space-y-6 max-w-xl">
            {FOUNDER.paras.map((p, i) => (
              <FadeUp key={i} delay={0.03 * i}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-cream/75">{p}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.15}>
            <p className="mt-10 font-display text-sm uppercase tracking-[0.2em] text-coral">{FOUNDER.sign}</p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Mentorship = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const photos = MENTORSHIP.photos && MENTORSHIP.photos.length
    ? MENTORSHIP.photos
    : [{ url: MENTORSHIP.image, year: "" }];

  return (
    <section ref={ref} className="relative bg-cream py-20 md:py-28 px-5 md:px-10" data-testid="mentorship-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-end">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta mb-5">{MENTORSHIP.kicker}</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] text-ink">
              <MaskText lines={[MENTORSHIP.title]} />
            </h2>
          </div>
          <div className="space-y-5 max-w-xl">
            {MENTORSHIP.body.map((p, j) => (
              <FadeUp key={j} delay={0.04 * j}>
                <p className="font-sans text-base md:text-lg leading-relaxed text-ink/70">{p}</p>
              </FadeUp>
            ))}
          </div>
        </div>

        <Stagger className="mt-14 grid sm:grid-cols-3 gap-5 md:gap-6" stagger={0.08} data-testid="mentorship-photos">
          {photos.map((ph, i) => (
            <StaggerItem key={i}>
              <div className="relative overflow-hidden rounded-3xl aspect-[4/5] card-shadow">
                <motion.img style={{ y }} src={ph.url} alt={ph.year || "Peer mentor"} className="h-[116%] w-full object-cover" />
              </div>
              {ph.year && (
                <p className="mt-4 text-center font-display text-[0.7rem] uppercase tracking-[0.2em] font-bold text-magenta">{ph.year}</p>
              )}
              {ph.why && (
                <p className="mt-1.5 text-center font-serif italic text-sm md:text-base leading-snug text-ink/60 max-w-xs mx-auto">“{ph.why}”</p>
              )}
            </StaggerItem>
          ))}
        </Stagger>

        <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {MENTORSHIP.points.map((pt) => (
            <StaggerItem key={pt.title} className="rounded-3xl border border-ink/10 bg-white card-shadow p-7 h-full">
              <div className="h-10 w-10 rounded-full brand-gradient diamond-clip mb-5" />
              <p className="font-display text-sm uppercase tracking-[0.12em] text-ink">{pt.title}</p>
              <p className="mt-3 font-sans text-sm leading-relaxed text-ink/65">{pt.text}</p>
            </StaggerItem>
          ))}
        </Stagger>

        <FadeUp delay={0.1}>
          <p className="mt-12 border-l-2 border-magenta pl-6 font-serif italic text-2xl md:text-3xl leading-snug text-ink max-w-3xl">
            {MENTORSHIP.pull}
          </p>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Movement() {
  return (
    <div className="bg-cream">
      <PageIntro />
      {MANIFESTO.map((m, i) => (
        <Chapter key={m.no} m={m} i={i} />
      ))}
      <Mentorship />
      <div className="py-8 bg-cream border-y border-ink/10">
        <Marquee text="Prepared, not predicted." variant="dark" animClass="animate-marquee-slow" />
      </div>
      <Founder />
    </div>
  );
}
